// Who among the selected appointments can be asked by text, by email, or both
// — what the Send Confirmations channel dialog greys its buttons out from.
//
// Only OPEN appointments count (C/R already answered; the server skips them
// anyway). A patient is textable while any of their numbers (cell, home —
// utils/smsPhone) is not marked bad by sms_number_health (utils/badNumbers
// fetchBadNumbers); an email must look like an address, the same test the
// backend applies (confirmations.contact_email).

import { smsE164 } from "./badNumbers";

const EMAIL_RE = /^[^@\s,;]+@[^@\s,;]+\.[^@\s,;]+$/;

export const hasEmailAddress = (email) => EMAIL_RE.test(String(email || "").trim());

const isOpen = (status) => !["C", "R"].includes(String(status || "").toUpperCase());

// Every number a row can be texted on: `phones` (cell + home, utils/smsPhone)
// or the single `phone`.
export const rowPhoneList = (p) => {
  const list = Array.isArray(p?.phones) ? p.phones : [p?.phone];
  return [...new Set(list.map((v) => String(v || "").trim()).filter(Boolean))];
};

/**
 * @param patients [{ id, phone | phones: [...], email, status, name }]
 * @param badNumbers { phone as given: info } from fetchBadNumbers
 * @returns { total, answered, text: ids[], badNumber: ids[], email: ids[], unreachable: ids[] }
 *   text       — open, at least one number not known bad (the backend picks it)
 *   badNumber  — open, every number known bad (texted only if staff say so)
 *   email      — open, usable email
 *   unreachable — open, neither a good phone nor an email
 */
export const summarizeConfirmationChannels = (patients, badNumbers = {}) => {
  const bad = new Set(Object.keys(badNumbers || {}).map(smsE164));
  const out = { total: 0, answered: 0, text: [], badNumber: [], email: [], unreachable: [] };
  (patients || []).forEach((p) => {
    if (!p || p.id == null) return;
    out.total += 1;
    if (!isOpen(p.status)) {
      out.answered += 1;
      return;
    }
    const phones = rowPhoneList(p);
    const textable = phones.some((phone) => !bad.has(smsE164(phone)));
    if (textable) out.text.push(p.id);
    else if (phones.length) out.badNumber.push(p.id);
    if (hasEmailAddress(p.email)) out.email.push(p.id);
    if (!textable && !hasEmailAddress(p.email)) out.unreachable.push(p.id);
  });
  return out;
};

/**
 * Ids per channel for a choice.
 * @param choice "sms" | "email" | "both"
 * @param includeBad also text the known-bad numbers (sent with confirm_bad_number)
 * @returns { sms: ids[], confirmBad: ids[], email: ids[] }
 */
export const planConfirmationSend = (summary, choice, { includeBad = false } = {}) => {
  const wantsText = choice === "sms" || choice === "both";
  const wantsEmail = choice === "email" || choice === "both";
  const confirmBad = wantsText && includeBad ? [...summary.badNumber] : [];
  return {
    sms: wantsText ? [...summary.text, ...confirmBad] : [],
    confirmBad,
    email: wantsEmail ? [...summary.email] : [],
  };
};

// Distinct patients a choice reaches.
export const reachCount = (summary, choice, opts) => {
  const plan = planConfirmationSend(summary, choice, opts);
  return new Set([...plan.sms, ...plan.email].map(String)).size;
};
